import { useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardsContext } from '../contexts/DashboardsContext';
import { getDashboardQueriesFunc } from '../lib/helper';
import { DashboardQuery } from '../types/files';
import ObjectUtils from '../utils/ObjectUtils';

const useDashboardQueryFetch = <T>(dashboardId: string, queryTitle: string) => {
  const { mappedOverviewQueries, setDashboardsQueries } = useContext(DashboardsContext);
  const queryKey = `${dashboardId}:${queryTitle}`;
  const [query, setQuery] = useState<T | undefined>(() => mappedOverviewQueries.get(queryKey) as T | undefined);
  const dashboardIdRef = useRef<string>(dashboardId);

  // eslint-disable-next-line
  const queryKeyMemo = useMemo(() => queryKey, [dashboardId, queryTitle]);

  const { refetch } = useQuery({
    queryKey: ['dashboardQuery', queryKeyMemo],
    queryFn: async () => !mappedOverviewQueries.has(queryKeyMemo)
      ? await getDashboardQueriesFunc(dashboardId, queryTitle) : [],
    staleTime: 5 * 60 * 1000,
    retry: 2,
    onSuccess: (bulkQueryData: DashboardQuery[]) => {
      if (!!bulkQueryData.length) {
        setDashboardsQueries(prev => produce(prev, draft => {
          bulkQueryData.forEach(newQuery => {
            if (!draft.find(query => query.dashboardId === newQuery.dashboardId && query.title === newQuery.title))
              draft.push(newQuery);
          });
        }));
      }
    },
  });

	useEffect(() => {
		if (!ObjectUtils.equalObjects(dashboardIdRef.current, dashboardId)) {
			dashboardIdRef.current = dashboardId;
			if (mappedOverviewQueries.has(queryKeyMemo)) {
				setQuery(mappedOverviewQueries.get(queryKeyMemo) || []);
			} else {
        setQuery(undefined);
        refetch();
			}
		} else if (mappedOverviewQueries.has(queryKeyMemo)) {
      setQuery(mappedOverviewQueries.get(queryKeyMemo) || '');
    }
		// eslint-disable-next-line
	}, [mappedOverviewQueries, dashboardId]);

  return { queryLoaded: true, query };
};

export default useDashboardQueryFetch;
